// UserDashboardPage
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './UserDashboard.css';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
import { useRegion } from '../context/RegionContext';
import { useCompanyRole } from '../hooks/useCompanyRole';

/* ─── SVG Icon Components (duotone, teal-aligned) ─── */

const IconLibrary = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" opacity=".35" fill="currentColor"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const IconLogs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="16" height="18" rx="2" opacity=".35" fill="currentColor"/>
    <path d="M8 8h8"/>
    <path d="M8 12h8"/>
    <path d="M8 16h5"/>
  </svg>
);

const IconMyLibrary = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" opacity=".15" fill="currentColor"/>
    <path d="M7 8h10"/>
    <path d="M7 12h10"/>
    <path d="M7 16h6"/>
    <circle cx="19" cy="19" r="3" fill="currentColor" opacity=".6"/>
    <path d="M18 19h2M19 18v2" stroke="white" strokeWidth="1.5"/>
  </svg>
);

const IconSubmit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" opacity=".15" fill="currentColor"/>
    <path d="M12 8v8"/>
    <path d="M8 12h8"/>
  </svg>
);

const IconBuild = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" opacity=".15" fill="currentColor"/>
    <path d="M2 10h20"/>
    <path d="M6 6V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"/>
    <path d="M12 14v2"/>
    <path d="M8 14v2"/>
    <path d="M16 14v2"/>
  </svg>
);

const IconMaterials = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4" opacity=".15" fill="currentColor"/>
    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m0 0v10l8 4"/>
  </svg>
);

const IconEPD = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" opacity=".15" fill="currentColor"/>
    <polyline points="14 2 14 8 20 8"/>
    <path d="M9 15l2 2 4-4"/>
  </svg>
);

const IconCalculator = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c4-4 8-7.5 8-12A8 8 0 0 0 4 10c0 4.5 4 8 8 12z" opacity=".15" fill="currentColor"/>
    <path d="M12 22c4-4 8-7.5 8-12A8 8 0 0 0 4 10c0 4.5 4 8 8 12z"/>
    <path d="M12 12V8"/>
    <path d="M9 10l3-2 3 2"/>
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4l4 4-4 4"/>
  </svg>
);

const IconTeam = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="4" opacity=".15" fill="currentColor"/>
    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    <path d="M21 21v-2a4 4 0 0 0-3-3.85"/>
  </svg>
);


/* ─── User Dashboard Component ─── */

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { selectedRegion } = useRegion();
  const { isCompanyAdmin, pendingInvites, pendingInviteCount } = useCompanyRole();
  const [myProductCount, setMyProductCount] = useState(0);
  const [myCompositeCount, setMyCompositeCount] = useState(0);

  useEffect(() => {
    const loadMyStats = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token || !user?.email) return;

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region': selectedRegion || 'vquest',
          'x-user-role': user.role || 'user',
          'x-user-id': user.uid,
          'x-user-email': user.email,
        };

        const [matRes, compRes] = await Promise.all([
          fetch(`${API_BASE_URL}/material-requests/all`, { headers }).then(r => r.ok ? r.json() : { requests: [] }),
          fetch(`${API_BASE_URL}/composite-requests/all`, { headers }).then(r => r.ok ? r.json() : { requests: [] }),
        ]);

        const userEmail = user.email?.toLowerCase();
        const myProducts = (matRes.requests || []);
        const myComposites = (compRes.requests || []);

        setMyProductCount(myProducts.length);
        setMyCompositeCount(myComposites.length);
      } catch (err) {
        console.error('Error loading user stats:', err);
      }
    };

    loadMyStats();
  }, [user, selectedRegion]);

  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const formattedTime = currentDate.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit",
  });

  // ─── Card data ───
  const cards = [
    { label: 'Material Library',   desc: 'Browse available materials and their properties',      path: '/categories',            Icon: IconLibrary,    color: 'teal' },
    { label: 'Build New Material', desc: 'Create custom material composites from available materials', path: '/composite-requests', Icon: IconBuild,     color: 'amber' },
    { label: 'My Materials',       desc: 'View and manage your saved composites',                 path: '/user/my-composites',    Icon: IconMaterials,  color: 'indigo' },
    { label: 'My Library',         desc: 'Browse your approved private materials by category',    path: '/user/my-library', Icon: IconMyLibrary,        color: 'teal' },
    { label: 'My Team', desc: 'Connect with other users to share private materials', path: '/user/my-team', Icon: IconTeam, color: 'rose', badge: pendingInviteCount },
    { label: 'Submit New Product', desc: 'Submit a new product to our database',                  path: '/material-requests',     Icon: IconSubmit,     color: 'emerald' },
    { label: 'EPD\u2019s',         desc: 'Search for an EPD from a selection of external sites',  path: '/user/epd',              Icon: IconEPD,        color: 'violet' },
    { label: 'Calculators',  desc: 'Choose from a list of calculators to help with quantification',     path: '/user/calculator',       Icon: IconCalculator, color: 'sky' },
    // Company admins only: audit their team's material activity
    ...(isCompanyAdmin
      ? [{ label: 'Logs', desc: 'Audit your team’s material activity — who did what, and when', path: '/user/logs', Icon: IconLogs, color: 'rose' }]
      : []),
  ];

  const stats = [
    { value: myProductCount,  label: 'Products Submitted' },
    { value: myCompositeCount, label: 'Composites Requested' },
    { value: myProductCount + myCompositeCount, label: 'Total Requests' },
  ];

  // ─── Render ───
  return (
    <div className="neo-user-dashboard">

      {/* ─── Header ─── */}
      <header className="ud-header">
        <div className="ud-header__inner">
          <div className="ud-header__text">
            <h1>Material Ops Console</h1>
            <p>Design and build custom material composites for your projects</p>
          </div>
          <div className="ud-header__meta">
            <div className="ud-header__pill">
              <span>📅</span> {formattedDate} &middot; {formattedTime}
            </div>
            <div className="ud-header__pill">
              <span>👤</span> {user?.email || 'User'}
            </div>
          </div>
        </div>
      </header>

      {/* ─── Pending company invite ─── */}
      {/* An invite is only actionable from My Team, and nothing else in the app
          points there. Without this an invitee who missed the email keeps
          working with no company visibility and no idea an invite is waiting. */}
      {pendingInviteCount > 0 && (
        <div
          className="ud-invite-banner"
          onClick={() => navigate('/user/my-team')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/user/my-team'); }}
        >
          <div className="ud-invite-banner__icon">✉️</div>
          <div className="ud-invite-banner__text">
            <strong>
              {pendingInviteCount === 1
                ? `You've been invited to join ${pendingInvites[0].CompanyName || 'a company'}`
                : `You have ${pendingInviteCount} pending company invites`}
            </strong>
            <span>
              Accept to share private materials with your team — colleagues can't
              see your materials until you do.
            </span>
          </div>
          <span className="ud-invite-banner__cta">Review invite</span>
        </div>
      )}

      {/* ─── Tools section ─── */}
      <section className="ud-section">
        <div className="ud-section__header">
          <div className="ud-section__icon ud-section__icon--tools">🛠️</div>
          <h2 className="ud-section__title">Quick Access</h2>
          <span className="ud-section__count">{cards.length} tools</span>
        </div>

        <div className="ud-grid">
          {cards.map((card) => (
            <div className="ud-card" key={card.path} onClick={() => navigate(card.path)}>
              <div className={`ud-card__icon ud-card__icon--${card.color}`}>
                <card.Icon />
              </div>
              <div className="ud-card__body">
                <h3>
                  {card.label}
                  {card.badge > 0 && <span className="ud-card__badge">{card.badge}</span>}
                </h3>
                <p>{card.desc}</p>
              </div>
              <span className="ud-card__arrow"><IconArrow /></span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Stats section ─── */}
      <div className="ud-stats-panel">
        <div className="ud-stats-panel__header">
          <div className="ud-stats-panel__icon">📊</div>
          <h3 className="ud-stats-panel__title">Your Statistics</h3>
        </div>
        <div className="ud-stats-grid">
          {stats.map((stat) => (
            <div className="ud-stat-block" key={stat.label}>
              <div className="ud-stat-block__value">{stat.value}</div>
              <div className="ud-stat-block__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
